import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

export const DesignsSection = () => {
  const { refreshStats } = useOutletContext();
  const confirm = useConfirm();
  const [designs, setDesigns] = useState([]);
  const [category, setCategory] = useState('all');
  const [archived, setArchived] = useState('false');
  // Upload
  const [dName, setDName] = useState('');
  const [dCategory, setDCategory] = useState('sticker');
  const [dTags, setDTags] = useState('');
  const [dImage, setDImage] = useState('');
  // AI
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCategory, setAiCategory] = useState('sticker');
  const [aiBusy, setAiBusy] = useState(false);

  const load = () => {
    api.get('/admin/designs', { params: { category, archived } }).then(({ data }) => setDesigns(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, archived]);

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setDImage(reader.result);
    reader.readAsDataURL(f);
  };
  const uploadDesign = async (e) => {
    e.preventDefault();
    if (!dName || !dImage) { toast.error('Fill name and select image'); return; }
    try {
      await api.post('/admin/designs', { name: dName, category: dCategory, tags: dTags.split(',').map(t => t.trim()).filter(Boolean), is_premium: false, image_base64: dImage });
      toast.success('Design uploaded');
      setDName(''); setDTags(''); setDImage('');
      load(); refreshStats();
    } catch { toast.error('Upload failed'); }
  };
  const aiGenerateForLibrary = async () => {
    if (!aiPrompt) { toast('Enter a prompt'); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post('/ai/generate-design', { prompt: aiPrompt, category: aiCategory });
      const res = await fetch(assetUrl(data.image_url));
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = reader.result;
        await api.post('/admin/designs', { name: aiPrompt.slice(0, 40), category: aiCategory, tags: ['ai-generated'], is_premium: false, image_base64: b64 });
        toast.success('AI design added');
        setAiPrompt('');
        load(); refreshStats();
      };
      reader.readAsDataURL(blob);
    } catch { toast.error('AI generation failed'); } finally { setAiBusy(false); }
  };
  const archiveDesign = async (id, val) => {
    const ok = await confirm({ title: val ? 'Archive this design?' : 'Restore this design?', description: val ? 'It will be hidden from the public library.' : 'It will return to the public library.', confirmLabel: val ? 'Archive' : 'Restore' });
    if (!ok) return;
    try { await api.patch(`/admin/designs/${id}/archive?archived=${val}`); toast.success(val ? 'Archived' : 'Restored'); load(); refreshStats(); }
    catch { toast.error('Failed'); }
  };
  const deleteDesign = async (id) => {
    const ok = await confirm({ title: 'Delete this design permanently?', description: 'This cannot be undone.', confirmLabel: 'Delete permanently', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/admin/designs/${id}`); toast.success('Deleted'); load(); refreshStats(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="jw-card p-5">
          <h3 className="font-semibold flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Design</h3>
          <form onSubmit={uploadDesign} className="mt-3 space-y-3">
            <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Name</label><Input value={dName} onChange={(e) => setDName(e.target.value)} data-testid="admin-upload-name-input" /></div>
            <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Category</label>
              <Select value={dCategory} onValueChange={setDCategory}>
                <SelectTrigger data-testid="admin-upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sticker">Sticker</SelectItem><SelectItem value="phone_case">Phone Case</SelectItem><SelectItem value="label">Label</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Tags (comma separated)</label><Input value={dTags} onChange={(e) => setDTags(e.target.value)} /></div>
            <div><label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Image</label><Input type="file" accept="image/*" onChange={handleFile} data-testid="admin-upload-file-input" /></div>
            {dImage && <img src={dImage} alt="preview" className="w-32 h-32 object-cover rounded-md border" style={{ borderColor: 'var(--jw-border)' }} />}
            <Button type="submit" data-testid="admin-upload-submit-button" className="jw-btn-primary"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
          </form>
        </Card>

        <Card className="jw-card p-5">
          <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-400" /> Generate via AI</h3>
          <div className="mt-3 space-y-3">
            <Textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="admin-ai-prompt-input" placeholder="e.g. retro sunset sticker with palm trees" />
            <Select value={aiCategory} onValueChange={setAiCategory}>
              <SelectTrigger data-testid="admin-ai-category-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="sticker">Sticker</SelectItem><SelectItem value="phone_case">Phone Case</SelectItem><SelectItem value="label">Label</SelectItem></SelectContent>
            </Select>
            <Button onClick={aiGenerateForLibrary} disabled={aiBusy} data-testid="admin-ai-generate-button" className="jw-btn-primary">{aiBusy ? 'Generating...' : 'Generate & add to library'}</Button>
          </div>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-40" data-testid="admin-designs-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="sticker">Sticker</SelectItem>
            <SelectItem value="phone_case">Phone Case</SelectItem>
            <SelectItem value="label">Label</SelectItem>
          </SelectContent>
        </Select>
        <Select value={archived} onValueChange={setArchived}>
          <SelectTrigger className="w-36" data-testid="admin-designs-archived-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="false">Active</SelectItem><SelectItem value="true">Archived</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {designs.map(d => (
          <div key={d.id} className="jw-card overflow-hidden">
            <div className="aspect-square" style={{ background: 'var(--jw-bg-elev)' }}>
              <img src={assetUrl(d.image_url)} alt={d.name} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-2 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate">{d.name}</p>
                <p className="text-[10px] capitalize" style={{ color: 'var(--jw-text-subtle)' }}>{d.category?.replace('_', ' ')}</p>
              </div>
              <div className="flex gap-0.5 shrink-0">
                <button onClick={() => archiveDesign(d.id, !d.archived)} data-testid={`admin-design-${d.id}-archive-button`} className="p-1 rounded hover:opacity-80" style={{ color: 'var(--jw-text-muted)' }}>{d.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}</button>
                <button onClick={() => deleteDesign(d.id)} data-testid={`admin-design-${d.id}-delete-button`} className="p-1 rounded hover:opacity-80 text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {designs.length === 0 && <div className="col-span-full text-center py-16" style={{ color: 'var(--jw-text-muted)' }}>No designs.</div>}
      </div>
    </div>
  );
};


export default DesignsSection;
